(() => {
  const LOCAL_HOSTS = new Set(['127.0.0.1', 'localhost'])
  const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])
  const MAX_FILE_BYTES = 15 * 1024 * 1024
  const PERSON_POSITIONS = [1, 2, 3, 4, 5]
  const POLL_INTERVAL_MS = 650
  const GATEWAY_POLL_INTERVAL_MS = 5_000
  const REQUEST_TIMEOUT_MS = 8_000
  const MESSAGES = {
    gatewayConnecting: 'Connecting to the local Gateway…',
    gatewayReady: 'Gateway connected. Local mock is ready. Choose a photo to preview the flow.',
    gatewayUnavailable: 'The local Gateway is unavailable. Start the Docker services and try again.',
    photoReady: 'Photo ready. Generate when you are ready.',
    failed:
      'The AI service could not complete your figure. Your photo is still ready. Select Generate Again to retry.',
    network:
      'The connection was interrupted. Check your connection, then select Generate Again to retry.',
    timeout:
      'Generation took longer than 30 seconds. Your photo is still ready. Select Generate Again to retry.',
    success: 'Person 1 is ready. Choose a scene or add another person.',
  }

  function wait(milliseconds, signal) {
    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(resolve, milliseconds)
      signal.addEventListener(
        'abort',
        () => {
          window.clearTimeout(timeout)
          reject(new DOMException('Aborted', 'AbortError'))
        },
        { once: true },
      )
    })
  }

  async function fetchJson(url, options, parentSignal) {
    const requestController = new AbortController()
    const timeout = window.setTimeout(() => requestController.abort(), REQUEST_TIMEOUT_MS)
    const abortRequest = () => requestController.abort()
    parentSignal.addEventListener('abort', abortRequest, { once: true })

    try {
      const response = await fetch(url, { ...options, signal: requestController.signal })
      const payload = await response.json().catch(() => null)
      if (!response.ok) {
        throw new Error(payload?.error?.message || `Request failed with status ${response.status}.`)
      }
      return payload
    } finally {
      window.clearTimeout(timeout)
      parentSignal.removeEventListener('abort', abortRequest)
    }
  }

  // The bar creeps asymptotically toward PROGRESS_CAP so it never looks frozen;
  // server milestones only raise the floor, and only success reaches 100.
  const PROGRESS_CAP = 92
  const CRAWL_RATE = 0.02
  function generationProgress(element, signal) {
    let displayed = 1
    const render = () => {
      element.textContent = `${Math.floor(displayed)}%`
    }
    render()
    const stop = () => {
      window.clearInterval(timer)
      signal.removeEventListener('abort', stop)
    }
    const update = (value, completed = false) => {
      displayed = completed ? 100 : Math.max(displayed, Math.min(PROGRESS_CAP, value))
      render()
      if (completed) stop()
    }
    const timer = window.setInterval(() => {
      displayed = Math.min(PROGRESS_CAP, displayed + (PROGRESS_CAP - displayed) * CRAWL_RATE)
      render()
    }, 1_000)
    signal.addEventListener('abort', stop, { once: true })
    if (signal.aborted) stop()
    return { update, stop }
  }

  function photoValidationError(file) {
    if (!ACCEPTED_TYPES.has(file.type)) return 'Please upload a JPG, PNG or WebP image.'
    if (!file.size) return "We couldn’t read this image. Please choose another photo."
    if (file.size > MAX_FILE_BYTES) return 'This photo is larger than 15 MB. Please choose a smaller file.'
    return ''
  }

  const UPLOAD_ERROR_MESSAGES = {
    FACE_NOT_FOUND: "We couldn’t find a person in this photo. Please choose a clear photo of one person.",
    MULTIPLE_FACES: 'We found more than one person. Please upload a photo of one person.',
    UNSAFE_PHOTO: 'This photo may contain adult or violent content. Please choose another photo.',
    PHOTO_VALIDATION_UNAVAILABLE: "We couldn’t check your photo right now. Please try again.",
    UNREADABLE_PHOTO: "We couldn’t read this image. Please choose another photo.",
    INVALID_PHOTO: "We couldn’t read this image. Please choose another photo.",
    UPLOAD_TRANSFER_FAILED: 'The upload was interrupted. Please try again.',
    PAYLOAD_TOO_LARGE: 'This photo is larger than 15 MB. Please choose a smaller file.',
    UPLOAD_LIMIT: 'Please wait a moment and try again.',
    RATE_LIMITED: 'Please wait a moment and try again.',
    UPLOAD_FAILED: 'The upload could not finish. Please try again.',
  }

  function localGatewayBase(configuredBase) {
    const url = new URL(configuredBase)
    if (LOCAL_HOSTS.has(url.hostname) && LOCAL_HOSTS.has(window.location.hostname)) {
      url.hostname = window.location.hostname
    }
    return url.origin
  }

  class AiPersonalizationGenerator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return
      this.initialized = true
      this.apiBase = this.dataset.apiBase
      this.gatewayApiBase = localGatewayBase(this.dataset.gatewayApiBase)
      this.maxWaitMs = Number(this.dataset.maxWaitMs) || 30_000
      this.gatewayEnabled = this.dataset.gatewayMode === 'gateway'
      this.localMockEnabled = !this.gatewayEnabled && LOCAL_HOSTS.has(window.location.hostname)
      this.remoteSlots = {}
      this.draftPersonIds = Object.fromEntries([1,2,3,4,5].map(index => [index, crypto.randomUUID()]))
      this.uploadControllers = {}
      this.cartButton = this.querySelector('[data-ai-cart]')
      this.checkoutButton = this.querySelector('[data-ai-checkout]')
      this.commerceNote = this.querySelector('[data-ai-commerce-note]')
      this.cartButton?.addEventListener('click', () => this.completeCommerce(false))
      this.checkoutButton?.addEventListener('click', () => this.completeCommerce(true))
      this.gatewayReady = false
      this.personCount = 0
      this.readyPeople = new Set()
      this.pendingPersonIndex = 2
      this.photoUrls = {}
      this.activeView = 'create'
      this.previewPerson = 1
      this.selectedPeople = new Set()
      this.manualEditing = false
      this.manualEditingPeople = new Set()
      this.manualEditingCheckbox = this.querySelector('[data-ai-manual-editing]')
      this.manualEditingCheckbox.addEventListener('change', () => this.changeManualEditing())
      this.manualPersonInputs = [...this.querySelectorAll('[data-ai-manual-person]')]
      this.manualPersonInputs.forEach((input) => input.addEventListener('change', () => this.changeManualEditing(input)))
      this.cancelPersonButton = this.querySelector('[data-ai-cancel-person]')
      this.cancelPersonButton.addEventListener('click', () => {
        this.resetAdditionalForm()
        this.setActiveView('creation')
      })
      this.personDetails = [...this.querySelectorAll('[data-ai-person]')]
      this.personDetails.forEach((button) => button.addEventListener('click', () => {
        const person = Number(button.dataset.aiPerson)
        if (!this.readyPeople.has(person) || this.deleteBusy) return
        this.analytics?.preview(this.previewPerson, person)
        this.previewPerson = person
        this.renderPreview()
      }))
      this.personCards = [...this.querySelectorAll('[data-ai-person-card]')]
      this.deletePersonButtons = [...this.querySelectorAll('[data-ai-delete-person]')]
      this.resultImages = [...this.querySelectorAll('[data-ai-result-person]')]
      this.resultVisual = this.querySelector('.ai-generator__result-visual')

      this.fileInput = this.querySelector('[data-ai-file]')
      this.generateButton = this.querySelector('[data-ai-generate]')
      this.uploadView = this.querySelector('[data-ai-view="upload"]')
      this.successView = this.querySelector('[data-ai-view="success"]')
      this.dropzone = this.querySelector('[data-ai-dropzone]')
      this.uploadDefault = this.querySelector('[data-ai-upload-default]')
      this.uploadReady = this.querySelector('[data-ai-upload-ready]')
      this.uploadValidating = this.querySelector('[data-ai-upload-validating]')
      this.uploadRetry = this.querySelector('[data-ai-upload-retry]')
      this.uploadThumbnail = this.querySelector('[data-ai-upload-thumbnail]')
      this.status = this.querySelector('[data-ai-status]')
      this.progress = this.querySelector('[data-ai-progress]')
      this.failureMessage = this.querySelector('[data-ai-failure-message]')
      this.previewStates = [...this.querySelectorAll('[data-ai-preview-state]')]
      this.resultThumbnail = this.querySelector('[data-ai-result-thumbnail]')
      this.resultHelp = this.querySelector('[data-ai-result-help]')
      this.personHelp = this.resultHelp.textContent

      this.addPersonButton = this.querySelector('[data-ai-add-person]')
      this.secondForm = this.querySelector('[data-ai-second-form]')
      this.secondFileInput = this.querySelector('[data-ai-second-file]')
      this.secondGenerateButton = this.querySelector('[data-ai-second-generate]')
      this.secondDropzone = this.querySelector('[data-ai-second-dropzone]')
      this.secondUploadDefault = this.querySelector('[data-ai-second-upload-default]')
      this.secondUploadReady = this.querySelector('[data-ai-second-upload-ready]')
      this.secondUploadValidating = this.querySelector('[data-ai-second-upload-validating]')
      this.secondUploadRetry = this.querySelector('[data-ai-second-upload-retry]')
      this.secondUploadThumbnail = this.querySelector('[data-ai-second-upload-thumbnail]')
      this.secondStatus = this.querySelector('[data-ai-second-status]')
      this.secondProgress = this.querySelector('[data-ai-second-progress]')
      this.secondFailureMessage = this.querySelector('[data-ai-second-failure-message]')
      this.secondUploadTitle = this.querySelector('[data-ai-second-upload-title]')
      this.secondPreviewStates = [...this.querySelectorAll('[data-ai-second-preview-state]')]
      this.resultCustomization = this.querySelector('[data-ai-result-customization]')

      this.deleteDialog = this.querySelector('[data-ai-delete-dialog]')
      this.deleteConfirmButton = this.querySelector('[data-ai-delete-confirm]')
      this.deleteCancelButtons = [...this.querySelectorAll('[data-ai-delete-cancel]')]
      this.deleteThumbnail = this.querySelector('[data-ai-delete-thumbnail]')
      this.deletePersonLabel = this.querySelector('[data-ai-delete-person-label]')
      this.deleteError = this.querySelector('[data-ai-delete-error]')

      this.sceneButtons = [...this.querySelectorAll('[data-ai-scene]')]
      this.scenePrice = this.querySelector('[data-ai-scene-price]')
      this.totalPrice = this.querySelector('[data-ai-total-price]')
      this.figureSummaryLabel = this.querySelector('[data-ai-figure-summary-label]')
      this.figureSummaryPrice = this.querySelector('[data-ai-figure-summary-price]')

      if (this.gatewayEnabled || this.localMockEnabled) {
        this.resultHelp.textContent =
          'Mock preview: your uploaded photo is shown here instead of an AI-generated result.'
      }

      this.defaultButtonLabel = this.generateButton.dataset.defaultLabel
      this.retryButtonLabel = this.generateButton.dataset.retryLabel
      this.secondDefaultButtonLabel = this.secondGenerateButton.dataset.defaultLabel
      this.secondRetryButtonLabel = this.secondGenerateButton.dataset.retryLabel
      this.currentState = 'unavailable'
      this.secondState = 'idle'

      this.fileInput.addEventListener('change', () => void this.handleFirstFileChange())
      this.uploadRetry.addEventListener('click', (event) => {
        event.preventDefault()
        event.stopPropagation()
        this.analytics?.retry(this.fileInput)
        void this.validateSelectedPhoto(1)
      })
      for (const [prefix, input] of [['', this.fileInput], ['second-', this.secondFileInput]]) {
        this.querySelector(`[data-ai-${prefix}upload-change]`).addEventListener('click', () => input.click())
      }
      this.generateButton.addEventListener('click', () => this.startFirstGeneration())
      this.deletePersonButtons.forEach((button) => button.addEventListener('click', () => {
        this.openDeleteDialog(Number(button.dataset.aiDeletePerson))
      }))
      this.deleteCancelButtons.forEach((button) => button.addEventListener('click', () => {
        if (!this.deleteBusy) this.deleteDialog.close()
      }))
      this.deleteConfirmButton.addEventListener('click', () => this.confirmDeletePerson())
      this.addPersonButton.addEventListener('click', () => this.beginSecondPerson())
      this.secondFileInput.addEventListener('change', () => void this.handleSecondFileChange())
      this.secondUploadRetry.addEventListener('click', (event) => {
        event.preventDefault()
        event.stopPropagation()
        this.analytics?.retry(this.secondFileInput)
        void this.validateSelectedPhoto(2)
      })
      this.secondGenerateButton.addEventListener('click', () => this.startSecondGeneration())

      if (this.gatewayEnabled) {
        this.setStatus('Connecting to the test Gateway…')
        this.setState('unavailable')
        void this.openGateway()
      } else if (this.localMockEnabled) {
        this.setStatus(MESSAGES.gatewayConnecting)
        this.setState('unavailable')
        void this.openLocalGatewaySession()
      } else {
        this.setStatus(this.dataset.unavailableMessage)
        this.setState('unavailable')
      }
      this.setSecondState('idle')
      this.setActiveView('create')
    }

    disconnectedCallback() {
      this.gatewayController?.abort()
      this.sessionController?.abort()
      Object.values(this.uploadControllers).forEach((controller) => controller.abort())
      this.abortGeneration()
      PERSON_POSITIONS.forEach((person) => this.revokePhotoUrl(person))
      this.scenePicker?.destroy()
      this.marketing?.destroy()
    }

    async openGateway() {
      this.gatewayController = new AbortController()
      try {
        const gatewayClientUrl = new URL(this.dataset.gatewayClientUrl, window.location.href).href
        this.gatewayModule = await import(gatewayClientUrl)
        this.gateway = new this.gatewayModule.GatewayClient(this.gatewayApiBase)
        const capabilities = await this.gateway.open()
        if (!this.isConnected) return
        this.gatewayReady = true
        this.generationMode = capabilities.generationMode || 'mock'
        if (this.dataset.marketingEnabled === 'true' && this.dataset.marketingUrl) {
          try {
            const module = await import(new URL(this.dataset.marketingUrl, location.href).href)
            this.marketing = module.attachMarketing(this, capabilities.analyticsEnvironment)
            this.gateway.marketing = this.marketing
          } catch { console.warn('AI marketing unavailable; customization remains available.') }
        }
        if (this.dataset.analyticsUrl) {
          try {
            const module = await import(new URL(this.dataset.analyticsUrl, location.href).href)
            this.analytics = module.attachAnalytics(this, capabilities.analyticsEnvironment)
            this.gateway.analytics = this.analytics
          } catch { console.warn('Analytics unavailable; customization remains available.') }
        }
        this.resultHelp.hidden = this.generationMode !== 'mock'
        this.setState('idle')
        this.setStatus('')
        if (capabilities.checkout) {
          try {
            this.catalog = await this.gateway.request('/catalog')
            this.commerceNote.textContent = this.generationMode === 'mock'
              ? 'Test orders only. Prices come from Shopify; uploaded-photo mock previews are not production assets.' : ''
          } catch (error) {
            this.commerceNote.textContent = error.message
          }
        }
        await this.initializeScenes()
        await this.recoverGatewayDesign()
      } catch (error) {
        if (!this.isConnected) return
        this.gatewayReady = false
        this.setState('unavailable')
        this.setStatus(error.message || 'The test Gateway is unavailable.', true)
      }
    }

    async recoverGatewayDesign() {
      const recovered = await this.gateway.request('/customizations')
      // Finalized designs are immutable cart/order snapshots. Reopening one as
      // the active editor leaves upload and delete controls intentionally
      // locked, which looks like a broken page. Resume the newest draft only;
      // when none exists, start a fresh design.
      const design = recovered.customizations.find((customization) => customization.status === 'draft')
      if (!design) return
      this.remoteDesign = design
      this.analytics?.restore(recovered)
      for (const person of design.people) {
        await this.loadGatewayPerson(person, true)
      }
      if (design.people.some((p) => p.personIndex === 1)) this.showFirstSuccess()
      for (const person of design.people) {
        if (person.personIndex !== 1) this.showSecondSuccess(person.personIndex)
      }
      this.readyPeople = new Set(design.people.map((person) => person.personIndex))
      this.previewPerson = [...this.readyPeople].at(-1) || 1
      this.renderPreview()
      this.selectedPeople = new Set(design.people.map((person) => person.personIndex))
      this.manualEditing = Boolean(design.manualEditing)
      this.manualEditingPeople = new Set(design.manualEditingPersonIndexes ?? (design.manualEditing ? design.personIndexes : []))
      await this.scenePicker?.restore(design)
      const pending = recovered.tasks.find((task) => task.customizationId === design.id && task.status === 'running')
      if (pending) {
        const additional = this.readyPeople.size > 0
        this.pendingPersonIndex = pending.personIndex
        if (additional) this.beginSecondPerson(pending.personIndex)
        if (additional) this.setSecondState('generating')
        else this.setState('generating')
        this.generationController = new AbortController()
        const progress = generationProgress(additional ? this.secondProgress : this.progress, this.generationController.signal)
        try {
          await this.pollGatewayTask(pending.id, progress, this.generationController.signal)
          this.remoteDesign = await this.gateway.request(`/customizations/${design.id}`)
          const completedPerson = this.remoteDesign.people.find((person) => person.personIndex === pending.personIndex)
          if (completedPerson) await this.loadGatewayPerson(completedPerson)
          if (additional) this.showSecondSuccess(pending.personIndex)
          else this.showFirstSuccess()
        } catch (error) {
          const message = `${error.message} Select the photo again if it is no longer available in this tab.`
          if (additional) this.showSecondFailure(message)
          else this.showFirstFailure(message)
        } finally {
          progress.stop()
        }
      }
      if (this.readyPeople.size === 0) this.setActiveView('create')
      this.updateCommerce()
    }

    async pollGatewayTask(id, progress, signal) {
      while (!signal.aborted) {
        const task = await this.gateway.request(`/generations/${id}`, { signal })
        progress.update(task.progress, task.status === 'succeeded')
        if (task.status === 'succeeded') return task
        if (task.status !== 'running') {
          const error = new Error(task.error?.message || 'Generation was cancelled. Try again.')
          error.terminal = true
          throw error
        }
        await wait(GATEWAY_POLL_INTERVAL_MS, signal)
      }
      throw new DOMException('Aborted', 'AbortError')
    }

    async runGatewayGeneration(file, personIndex, progress, scenario, signal) {
      await this.ensureEditableDesign()
      let slot = this.remoteSlots[personIndex]
      if (!slot || slot.file !== file) {
        slot = { file, uploadKey: crypto.randomUUID(), generationKey: crypto.randomUUID() }
        this.remoteSlots[personIndex] = slot
      }
      if (!slot.asset) {
        try {
          slot.uploadFile ??= await this.prepareUploadFile(file)
          if (slot.uploadFile.size > MAX_FILE_BYTES) {
            const error = new Error(UPLOAD_ERROR_MESSAGES.PAYLOAD_TOO_LARGE)
            error.code = 'PAYLOAD_TOO_LARGE'
            throw error
          }
          slot.asset = await this.gateway.upload(file, slot.uploadKey, signal, 'person', slot.uploadFile)
        } catch (error) {
          error.upload = true
          throw error
        }
      }
      if (!slot.request) slot.request = { assetId: slot.asset.id, personIndex, personId: this.draftPersonIds[personIndex], ...(this.remoteDesign ? { customizationId: this.remoteDesign.id } : {}), scenario }
      if (!slot.task) {
        const started = Date.now()
        try { slot.task = await this.gateway.request('/generations', { body: slot.request, key: slot.generationKey, signal }) }
        catch (error) { this.analytics?.generationRejected(personIndex, error, Date.now()-started); throw error }
      }
      const created = slot.task
      this.remoteDesign = { ...this.remoteDesign, id: created.customizationId }
      try {
        const result = await this.pollGatewayTask(created.id, progress, signal)
        this.remoteDesign = await this.gateway.request(`/customizations/${created.customizationId}`, { signal })
        await this.loadGatewayPerson(this.remoteDesign.people.find((person) => person.personIndex === personIndex))
        return result
      } catch (error) {
        // Only a known failed/cancelled task authorizes a new attempt. Network
        // uncertainty retries the same idempotency key instead of charging again.
        if (error.terminal) { slot.previousTaskId = slot.task?.id; slot.generationKey = crypto.randomUUID(); slot.task = null; slot.request = null }
        throw error
      }
    }

    updateCommerce() {
      const locked = this.commerceBusy
      const busy = ['generating', 'validating'].includes(this.currentState) || ['generating', 'validating'].includes(this.secondState) || this.deleteBusy || this.manualEditingBusy || this.scenePicker?.busy
      this.deletePersonButtons.forEach((button) => {
        button.disabled = !!locked || busy
      })
      const figure = this.catalog?.figure
      const currency = figure?.currency || this.dataset.currency
      const money = (cents) => new Intl.NumberFormat(undefined, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(cents / 100)
      const figureCents = figure ? Math.round(Number(figure.amount) * 100) : Number(this.dataset.personPriceCents)
      this.querySelectorAll('[data-ai-person-price]').forEach((label) => { label.textContent = money(figureCents) })
      this.scenePicker?.update(this.selectedPeople.size, !!locked || !!busy)
      const scene = this.scenePicker?.selected
      const sceneCents = Math.round(Number(scene?.amount || 0) * 100)
      const figureAmount = figureCents * this.selectedPeople.size
      this.figureSummaryLabel.textContent = this.selectedPeople.size > 1 ? `${this.dataset.twoFigureLabel} × ${this.selectedPeople.size}` : this.dataset.oneFigureLabel
      this.figureSummaryPrice.textContent = money(figureAmount)
      this.scenePrice.textContent = money(sceneCents)
      const service = this.catalog?.manualEditing
      const serviceAvailable = this.localMockEnabled || service?.available
      const serviceCents = service ? Math.round(Number(service.amount) * 100) : this.localMockEnabled ? 800 : 0
      const servicePrice = `+${money(serviceCents)}`
      const serviceAmount = this.manualEditing ? serviceCents * this.manualEditingPeople.size : 0
      this.manualEditingCheckbox.checked = this.manualEditing && (this.personCount > 1 || this.manualEditingPeople.size > 0)
      this.manualEditingCheckbox.disabled = !!locked || !!busy || !this.selectedPeople.size || (!serviceAvailable && !this.manualEditing)
      this.querySelector('[data-ai-manual-unit-price]').textContent = serviceAvailable ? `${servicePrice}/Figure` : 'Currently unavailable'
      this.querySelector('[data-ai-manual-people]').hidden = !this.manualEditing || this.personCount < 2
      this.manualPersonInputs.forEach((input) => {
        const person = Number(input.dataset.aiManualPerson)
        const row = input.closest('[data-ai-manual-person-row]')
        row.hidden = !this.readyPeople.has(person)
        input.checked = this.manualEditingPeople.has(person)
        input.disabled = !!locked || !!busy || (!serviceAvailable && !input.checked)
        row.querySelector('[data-ai-manual-person-price]').textContent = servicePrice
      })
      this.querySelector('[data-ai-manual-summary]').hidden = !this.manualEditing || !this.manualEditingPeople.size
      this.querySelector('[data-ai-manual-summary-count]').textContent = ` × ${this.manualEditingPeople.size}`
      this.querySelector('[data-ai-manual-summary-price]').textContent = money(serviceAmount)
      this.totalPrice.textContent = money(figureAmount + sceneCents + serviceAmount)
      const available = this.gatewayEnabled && this.gatewayReady && this.selectedPeople.size > 0 && figure?.available && (!this.manualEditing || serviceAvailable) && !this.commerceBusy && !busy && (this.scenePicker?.ready ?? true)
      for (const button of [this.cartButton, this.checkoutButton]) {
        button.disabled = !available
        button.setAttribute('aria-disabled', String(!available))
      }
      this.addPersonButton.disabled = !!locked || busy
      this.cancelPersonButton.disabled = !!locked || !!busy
    }

    async changeManualEditing(personInput) {
      if (this.manualEditingBusy) return
      const previous = this.manualEditing
      const previousPeople = new Set(this.manualEditingPeople)
      if (personInput) {
        const person = Number(personInput.dataset.aiManualPerson)
        if (personInput.checked) this.manualEditingPeople.add(person)
        else this.manualEditingPeople.delete(person)
      } else {
        this.manualEditing = this.manualEditingCheckbox.checked
        this.manualEditingPeople = new Set(this.manualEditing ? this.selectedPeople : [])
      }
      this.analytics?.manualChange(personInput, previous, previousPeople)
      this.manualEditingBusy = true
      this.updateCommerce()
      this.commerceNote.textContent = 'Saving your service selection…'
      try {
        await this.saveSceneSelection(this.scenePicker.selection)
        this.commerceNote.textContent = ''
        this.commerceNote.setAttribute('role', 'status')
      } catch (error) {
        this.analytics?.failed('manual_service', error)
        this.manualEditing = previous
        this.manualEditingPeople = previousPeople
        this.commerceNote.textContent = error.message || 'Your service selection could not be saved. Try again.'
        this.commerceNote.setAttribute('role', 'alert')
      } finally {
        this.manualEditingBusy = false
        this.updateCommerce()
      }
    }

    async completeCommerce(checkout) {
      if (this.manualEditingBusy || this.deleteBusy || !this.gatewayEnabled || !this.gatewayReady || !this.catalog?.figure.available || !this.remoteDesign || this.commerceBusy || this.scenePicker?.busy || this.selectedPeople.size === 0 || this.currentState === 'generating' || this.secondState === 'generating') return
      this.analytics?.commerce(checkout)
      let bundleId = null
      let cartConfirmed = false
      let commerceStage = 'prepare'
      this.commerceBusy = true
      this.updateCommerce()
      this.commerceNote.setAttribute('role', 'status')
      this.commerceNote.textContent = 'Confirming your design with Shopify…'
      try {
        const selection = this.scenePicker?.selection ?? { sceneVariantId: null, scenePhotoAssetId: null, sceneTemplateDigest: null }
        const prepared = await this.gateway.prepareCart(this.remoteDesign, {
          ...selection, manualEditing: this.manualEditing, manualEditingPersonIndexes: [...this.manualEditingPeople].sort(), personIndexes: [...this.selectedPeople],
        })
        const design = prepared.customization
        this.remoteDesign = design
        for (const person of design.people.filter(person => person.mock && design.personIndexes.includes(person.personIndex))) {
          try {
            const preview = {
              customizationId: design.id,
              gatewayBase: this.gatewayApiBase,
              assetId: person.assetId,
              figureVariantId: this.catalog.figure.id,
            }
            this.gatewayModule.rememberMockCartPreview({ ...preview, figureId: person.taskId })
            // Retain preview support for immutable handoffs created before per-figure rows.
            if (person.personIndex === design.personIndexes[0]) this.gatewayModule.rememberMockCartPreview(preview)
          } catch (error) {
            console.warn('The mock cart preview could not be remembered.', error instanceof Error ? error.message : 'Unknown preview error.')
          }
        }
        commerceStage = 'handoff'
        const handoff = prepared.handoff
        bundleId = handoff.id
        const response = await fetch(`${handoff.proxyPath}/handoff`, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ id: handoff.id, token: handoff.token }), signal: AbortSignal.timeout(15_000) })
        const payload = await response.json()
        if (!response.ok) throw new Error(payload.error?.message || 'Shopify App Proxy is not configured.')
        commerceStage = 'cart'
        await this.gatewayModule.addHandoffToCart(payload)
        cartConfirmed = true
        this.analytics?.cart('succeeded', bundleId)
        if (checkout) {
          this.analytics?.emit('checkout_redirect_result', { checkout_id: null, outcome: 'initiated', error_code: null })
          window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`)
        } else {
          await this.showConfirmedCart()
        }
      } catch (error) {
        if (commerceStage !== 'cart') this.analytics?.purchaseBlocked(checkout, error)
        else if (!cartConfirmed) this.analytics?.cart(error.status ? 'failed' : 'unknown', bundleId, error)
        else if (checkout) this.analytics?.emit('checkout_redirect_result', { checkout_id: null, outcome: 'failed', error_code: 'NAVIGATION_FAILED' })
        this.commerceNote.textContent = error.message || 'Cart update failed. Try again.'
        this.commerceNote.setAttribute('role', 'alert')
      } finally {
        this.commerceBusy = false
        this.updateCommerce()
      }
    }

    async showConfirmedCart() {
      const drawer = document.querySelector('cart-drawer')
      if (!drawer) {
        window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`)
        return
      }
      try {
        const requested = drawer.getSectionsToRender()
        const url = new URL(window.Shopify?.routes?.root || '/', window.location.origin)
        url.searchParams.set('sections', requested.map(section => section.id).join(','))
        const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(15_000) })
        if (!response.ok) throw new Error('Cart sections could not be refreshed.')
        const sections = await response.json()
        // Shopify can return HTTP 200 with null sections. Validate before replacing the drawer.
        for (const section of requested) {
          const html = sections?.[section.id]
          if (typeof html !== 'string' || !new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector || '.shopify-section')) {
            throw new Error('Cart sections are incomplete.')
          }
        }
        drawer.setActiveElement(this.cartButton)
        drawer.classList.remove('is-empty')
        drawer.renderContents({ sections })
        this.commerceNote.textContent = 'Added to your cart.'
      } catch (error) {
        throw new Error('Your items were added, but the cart drawer could not be refreshed. Try Add to Cart again to reopen it.', { cause: error })
      }
    }

    setState(state) {
      this.currentState = state
      this.dataset.state = state
      this.renderEntryLayout()
      this.generateButton.hidden = ['idle', 'unavailable', 'validating'].includes(state)
      this.previewStates.forEach((preview) => {
        preview.hidden =
          preview.dataset.aiPreviewState !== state &&
          !(['ready', 'unavailable', 'validating', 'upload-error'].includes(state) && preview.dataset.aiPreviewState === 'idle')
      })

      const canGenerate = !this.deleteBusy && this.gatewayReady && (!this.gatewayEnabled || !!this.file) && (state === 'ready' || state === 'failed')
      this.generateButton.disabled = !canGenerate
      this.generateButton.setAttribute('aria-disabled', String(!canGenerate))
      this.generateButton.textContent =
        state === 'failed' ? this.retryButtonLabel : this.defaultButtonLabel
      this.fileInput.disabled = !!this.deleteBusy || ['generating', 'validating', 'unavailable'].includes(state)
      this.dropzone.setAttribute('aria-busy', String(state === 'generating' || state === 'validating'))
    }

    setSecondState(state) {
      this.secondState = state
      this.dataset.secondState = state
      this.secondUploadTitle.textContent = `Choose a photo of person ${this.personCount + 1}`
      this.secondPreviewStates.forEach((preview) => {
        preview.hidden = preview.dataset.aiSecondPreviewState !== state
      })

      const canGenerate = !this.deleteBusy && this.gatewayReady && this.personCount < 5 && (!this.gatewayEnabled || !!this.secondFile) && (state === 'ready' || state === 'failed')
      this.secondGenerateButton.disabled = !canGenerate
      this.secondGenerateButton.setAttribute('aria-disabled', String(!canGenerate))
      this.secondGenerateButton.replaceChildren()
      for (const [className, label] of [
        ['ai-desktop-copy', state === 'failed' ? this.secondRetryButtonLabel : this.secondDefaultButtonLabel],
        ['ai-mobile-copy', state === 'failed' ? this.secondRetryButtonLabel : this.secondDefaultButtonLabel],
      ]) {
        const span = document.createElement('span')
        span.className = className
        span.textContent = label
        this.secondGenerateButton.append(span)
      }
      this.secondFileInput.disabled = !!this.deleteBusy || ['generating', 'validating'].includes(state) || this.personCount === 5
      this.secondDropzone.setAttribute('aria-busy', String(state === 'generating' || state === 'validating'))
      this.renderPreview()
      this.updateCommerce()
    }

    setStatus(message, isError = false) {
      this.status.textContent = message
      this.status.classList.toggle('ai-generator__status--error', isError)
      this.status.setAttribute('role', isError ? 'alert' : 'status')
    }

    setSecondStatus(message, isError = false) {
      this.secondStatus.textContent = message
      this.secondStatus.classList.toggle('ai-generator__status--error', isError)
      this.secondStatus.setAttribute('role', isError ? 'alert' : 'status')
    }

    async openLocalGatewaySession() {
      this.sessionController?.abort()
      this.sessionController = new AbortController()
      try {
        await fetchJson(
          `${this.gatewayApiBase}/api/v1/sessions`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { accept: 'application/json' },
          },
          this.sessionController.signal,
        )
        if (!this.isConnected) return
        this.gatewayReady = true
        await this.initializeScenes()
        this.setStatus(MESSAGES.gatewayReady)
        this.setState('idle')
      } catch (error) {
        if (!this.isConnected || this.sessionController.signal.aborted) return
        this.gatewayReady = false
        this.setStatus(MESSAGES.gatewayUnavailable, true)
        this.setState('unavailable')
      }
    }

    async handleFirstFileChange() {
      const file = this.fileInput.files?.[0]
      if (!file) return

      const validationError = photoValidationError(file)
      if (validationError) {
        this.file = null
        this.revokePhotoUrl(1)
        this.showUploadError(1, validationError)
        return
      }

      this.file = file
      this.clearUploadError(1)
      this.revokePhotoUrl(1)
      this.photoUrls[1] = URL.createObjectURL(file)
      this.setPersonPhoto(1, this.photoUrls[1])
      delete this.remoteSlots[1]
      await this.validateSelectedPhoto(1)
    }

    async handleSecondFileChange() {
      const file = this.secondFileInput.files?.[0]
      if (!file) return

      const validationError = photoValidationError(file)
      if (validationError) {
        this.secondFile = null
        this.revokePhotoUrl(this.pendingPersonIndex)
        this.showUploadError(2, validationError)
        return
      }

      this.secondFile = file
      this.clearUploadError(2)
      this.revokePhotoUrl(this.pendingPersonIndex)
      this.photoUrls[this.pendingPersonIndex] = URL.createObjectURL(file)
      this.setPersonPhoto(this.pendingPersonIndex, this.photoUrls[this.pendingPersonIndex])
      this.secondUploadThumbnail.removeAttribute('srcset')
      this.secondUploadThumbnail.removeAttribute('sizes')
      this.secondUploadThumbnail.src = this.photoUrls[this.pendingPersonIndex]
      delete this.remoteSlots[this.pendingPersonIndex]
      await this.validateSelectedPhoto(2)
    }

    async validateSelectedPhoto(formNumber) {
      if (this.deleteBusy) return
      const second = formNumber === 2
      const file = second ? this.secondFile : this.file
      const personIndex = second ? this.pendingPersonIndex : 1
      if (!file) return

      this.uploadControllers[formNumber]?.abort()
      const controller = new AbortController()
      this.uploadControllers[formNumber] = controller
      this.clearUploadError(formNumber)
      this.showUploadValidating(formNumber)

      let slot = this.remoteSlots[personIndex]
      if (!slot || slot.file !== file) {
        slot = { file, uploadKey: crypto.randomUUID(), generationKey: crypto.randomUUID() }
        this.remoteSlots[personIndex] = slot
      }
      try {
        const image = new Image()
        image.src = this.photoUrls[personIndex]
        try { await image.decode() } catch {
          const error = new Error(UPLOAD_ERROR_MESSAGES.UNREADABLE_PHOTO)
          error.code = 'UNREADABLE_PHOTO'
          throw error
        }
        if (controller.signal.aborted || this.uploadControllers[formNumber] !== controller) return
        this.analytics?.validation(file, null, null)
        if (this.gatewayEnabled) {
          slot.uploadFile ??= await this.prepareUploadFile(file)
          if (slot.uploadFile.size > MAX_FILE_BYTES) {
            const error = new Error(UPLOAD_ERROR_MESSAGES.PAYLOAD_TOO_LARGE)
            error.code = 'PAYLOAD_TOO_LARGE'
            throw error
          }
          slot.asset = await this.gateway.upload(file, slot.uploadKey, controller.signal, 'person', slot.uploadFile)
        }
        if (this.uploadControllers[formNumber] !== controller || !this.isConnected) return
        this.showUploadReady(formNumber)
      } catch (error) {
        if (controller.signal.aborted || this.uploadControllers[formNumber] !== controller) return
        if (error.code === 'UNREADABLE_PHOTO') { this.analytics?.validation(file, error.code, null); this.revokePhotoUrl(personIndex) }
        this.analytics?.photoState(formNumber, 'error', error.code ?? 'UPLOAD_INTERRUPTED')
        const contentError = ['FACE_NOT_FOUND', 'MULTIPLE_FACES', 'UNSAFE_PHOTO', 'UNREADABLE_PHOTO', 'INVALID_PHOTO', 'PAYLOAD_TOO_LARGE'].includes(error.code)
        if (contentError) {
          if (second) this.secondFile = null
          else this.file = null
          delete this.remoteSlots[personIndex]
        }
        this.showUploadError(
          formNumber,
          UPLOAD_ERROR_MESSAGES[error.code] || (['TypeError', 'TimeoutError', 'AbortError'].includes(error.name) ? 'The upload was interrupted. Please try again.' : UPLOAD_ERROR_MESSAGES.UPLOAD_FAILED),
          { retry: !contentError, clearInput: contentError, retryAfterSeconds: error.retryAfterSeconds },
        )
      }
    }

    async compressionLibrary() {
      if (this.compressionModule === undefined && this.dataset.imageCompressionUrl) {
        this.compressionModule = import(new URL(this.dataset.imageCompressionUrl, window.location.href).href)
          .then((module) => module.default)
      }
      return (await this.compressionModule) ?? null
    }

    async prepareUploadFile(file) {
      return this.gateway.prepareUploadFile(file, () => this.compressionLibrary())
    }

    showUploadValidating(formNumber) {
      const second = formNumber === 2
      const prefix = second ? 'second-' : ''
      this.querySelector(`[data-ai-${prefix}upload-default]`).hidden = true
      this.querySelector(`[data-ai-${prefix}upload-ready]`).hidden = true
      this.querySelector(`[data-ai-${prefix}upload-error]`).hidden = true
      this.querySelector(`[data-ai-${prefix}upload-validating]`).hidden = false
      if (second) {
        this.setSecondStatus('')
        this.setSecondState('validating')
      } else {
        this.setStatus('')
        this.setState('validating')
      }
    }

    showUploadReady(formNumber) {
      this.analytics?.photoState(formNumber, 'ready')
      const second = formNumber === 2
      const prefix = second ? 'second-' : ''
      this.querySelector(`[data-ai-${prefix}upload-validating]`).hidden = true
      this.querySelector(`[data-ai-${prefix}upload-error]`).hidden = true
      this.querySelector(`[data-ai-${prefix}upload-ready]`).hidden = false
      if (second) {
        this.setSecondStatus('')
        this.setSecondState('ready')
      } else {
        this.setStatus('')
        this.setState('ready')
      }
    }

    showUploadError(person, message, { retry = false, clearInput = true, retryAfterSeconds = 0 } = {}) {
      const prefix = person === 2 ? 'second-' : ''
      const dropzone = person === 2 ? this.secondDropzone : this.dropzone
      const input = person === 2 ? this.secondFileInput : this.fileInput
      // Re-selecting the same file must fire change after an upload failure.
      if (clearInput) input.value = ''
      input.setAttribute('aria-invalid', 'true')
      dropzone.dataset.error = 'true'
      this.querySelector(`[data-ai-${prefix}upload-default]`).hidden = true
      this.querySelector(`[data-ai-${prefix}upload-ready]`).hidden = true
      this.querySelector(`[data-ai-${prefix}upload-validating]`).hidden = true
      this.querySelector(`[data-ai-${prefix}upload-error]`).hidden = false
      const thumbnail = this.querySelector(`[data-ai-${prefix}upload-error-thumbnail]`)
      const photoUrl = this.photoUrls[person === 2 ? this.pendingPersonIndex : 1]
      thumbnail.hidden = !photoUrl
      if (photoUrl) thumbnail.src = photoUrl
      else thumbnail.removeAttribute('src')
      // Preserve the product copy's reason/recovery sentence break on mobile.
      this.querySelector(`[data-ai-${prefix}upload-error-message]`).textContent = message.replace('. Please', '.\nPlease')
      const retryButton = this.querySelector(`[data-ai-${prefix}upload-retry]`)
      retryButton.hidden = !retry
      retryButton.disabled = retry && retryAfterSeconds > 0
      if (retryButton.disabled) {
        window.setTimeout(() => { retryButton.disabled = false }, Math.min(retryAfterSeconds, 60) * 1000)
      }
      if (person === 2) { this.setSecondStatus(''); this.setSecondState('upload-error') }
      else { this.setStatus(''); this.setState('upload-error') }
      this.setActiveView('create')
    }

    clearUploadError(person) {
      const prefix = person === 2 ? 'second-' : ''
      const dropzone = person === 2 ? this.secondDropzone : this.dropzone
      const input = person === 2 ? this.secondFileInput : this.fileInput
      input.removeAttribute('aria-invalid')
      delete dropzone.dataset.error
      this.querySelector(`[data-ai-${prefix}upload-error]`).hidden = true
      const retryButton = this.querySelector(`[data-ai-${prefix}upload-retry]`)
      retryButton.hidden = true
      retryButton.disabled = false
    }

    revokePhotoUrl(person) {
      if (!this.photoUrls[person]) return
      URL.revokeObjectURL(this.photoUrls[person])
      delete this.photoUrls[person]
    }

    setPersonPhoto(person, url) {
      const index = person - 1
      const sourcePhoto = this.querySelector(`[data-ai-source-photo="${person}"]`)
      sourcePhoto.src = url
      sourcePhoto.hidden = false
      const images = [
        this.resultImages[index]?.querySelector('img'),
        this.manualPersonInputs[index]?.closest('label').querySelector('img'),
        person === 1 ? this.uploadThumbnail : this.secondUploadThumbnail,
        this.personDetails[index]?.querySelector('img'),
      ]
      images.forEach((image) => {
        if (!image) return
        image.removeAttribute('srcset')
        image.removeAttribute('sizes')
        image.src = url
      })
    }

    async loadGatewayPerson(person, restored = false) {
      if (!person) throw new Error('The generated figure is unavailable. Please try again.')
      await this.loadGatewayPhoto(person.personIndex, person.assetId)
      if (!person.mock && person.renderUrl) {
        const images = [
          this.resultImages[person.personIndex - 1]?.querySelector('img'),
          this.manualPersonInputs[person.personIndex - 1]?.closest('label').querySelector('img'),
          this.personDetails[person.personIndex - 1]?.querySelector('img'),
        ]
        for (const image of images) {
          if (!image) continue
          image.removeAttribute('srcset')
          image.removeAttribute('sizes')
          image.src = person.renderUrl
        }
      }
      this.analytics?.result(person)
      void this.marketing?.result(person, this.resultImages[person.personIndex - 1]?.querySelector('img'), restored)
      this.resultHelp.hidden = false
      if (!this.remoteDesign?.people.some((figure) => figure.mock)) this.resultHelp.textContent = this.personHelp
    }

    async loadGatewayPhoto(person, assetId) {
      try {
        const photo = await this.gateway.photoUrl(assetId)
        if (!photo) return false
        this.revokePhotoUrl(person)
        this.photoUrls[person] = photo
        this.setPersonPhoto(person, photo)
        return true
      } catch (error) {
        console.warn(
          'The uploaded photo could not be reloaded from the Gateway.',
          error instanceof Error ? error.message : 'Unknown preview error.',
        )
        return false
      }
    }

    abortGeneration() {
      this.generationController?.abort()
      this.generationController = null
    }

    selectedScenario(person) {
      const searchParams = new URLSearchParams(window.location.search)
      const requested =
        person === 2
          ? searchParams.get('ai_mock_second') || searchParams.get('ai_mock')
          : searchParams.get('ai_mock')
      if (requested === 'failed') return 'failure'
      if (requested === 'failure' || requested === 'timeout') return requested
      return 'success'
    }

    async runGeneration(file, personIndex, element, scenario, signal) {
      const progress = generationProgress(element, signal)
      try {
        if (this.gatewayEnabled) return await this.runGatewayGeneration(file, personIndex, progress, scenario, signal)
        const startedAt = Date.now()
        const created = await fetchJson(
          `${this.apiBase}/api/v1/generations`,
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              file: { name: file.name, type: file.type, size_bytes: file.size },
              scenario,
            }),
          },
          signal,
        )

        while (!signal.aborted) {
          if (Date.now() - startedAt >= this.maxWaitMs) {
            throw new Error(MESSAGES.timeout)
          }

          await wait(POLL_INTERVAL_MS, signal)
          const task = await fetchJson(
            `${this.apiBase}/api/v1/generations/${encodeURIComponent(created.task_id)}`,
            { headers: { accept: 'application/json' } },
            signal,
          )
          progress.update(task.progress, task.status === 'succeeded')

          if (task.status === 'failed') {
            throw new Error(task.error?.message || MESSAGES.failed)
          }
          if (task.status === 'succeeded') return task
        }

        throw new DOMException('Aborted', 'AbortError')
      } finally {
        progress.stop()
      }
    }

    async startFirstGeneration() {
      if (
        this.deleteBusy ||
        (!this.localMockEnabled && !this.gatewayEnabled) ||
        !this.gatewayReady ||
        !this.file ||
        this.currentState === 'generating'
      )
        return

      this.abortGeneration()
      this.generationController = new AbortController()
      const { signal } = this.generationController
      this.analytics?.generation(1)
      this.setStatus('')
      this.setState('generating')
      this.scrollGenerationIntoView()

      try {
        await this.runGeneration(this.file, 1, this.progress, this.selectedScenario(1), signal)
        this.showFirstSuccess()
      } catch (error) {
        if (signal.aborted && !this.isConnected) return
        if (error.upload) { this.showUploadError(1, error.message, { retry: true, clearInput: false }); return }
        const message =
          error instanceof Error && error.name !== 'AbortError' ? error.message : MESSAGES.network
        this.showFirstFailure(message)
      }
    }

    async startSecondGeneration() {
      if (
        this.deleteBusy ||
        (!this.localMockEnabled && !this.gatewayEnabled) ||
        !this.gatewayReady ||
        !this.secondFile ||
        (this.personCount < 1 || this.personCount >= 5) ||
        this.secondState === 'generating'
      )
        return

      this.abortGeneration()
      this.generationController = new AbortController()
      const { signal } = this.generationController
      this.analytics?.generation(this.pendingPersonIndex)
      this.setSecondStatus('')
      this.setSecondState('generating')
      this.scrollGenerationIntoView()

      try {
        await this.runGeneration(
          this.secondFile,
          this.pendingPersonIndex,
          this.secondProgress,
          this.selectedScenario(this.pendingPersonIndex),
          signal,
        )
        this.showSecondSuccess(this.pendingPersonIndex)
      } catch (error) {
        if (signal.aborted && !this.isConnected) return
        if (error.upload) { this.showUploadError(2, error.message, { retry: true, clearInput: false }); return }
        const message =
          error instanceof Error && error.name !== 'AbortError' ? error.message : MESSAGES.network
        this.showSecondFailure(message)
      }
    }

    showFirstFailure(message) {
      this.failureMessage.textContent = message || MESSAGES.failed
      this.setStatus('')
      this.setState('failed')
    }

    showSecondFailure(message) {
      this.secondFailureMessage.textContent = message || MESSAGES.failed
      this.setSecondStatus('')
      this.setSecondState('failed')
      this.setActiveView('create')
    }

    showFirstSuccess() {
      this.abortGeneration()
      this.readyPeople.add(1)
      this.personCount = this.readyPeople.size
      this.selectedPeople.add(1)
      this.previewPerson = 1
      this.setStatus(MESSAGES.success)
      this.setState('succeeded')
      if (this.scenePicker && !this.gatewayEnabled) {
        const scene = this.scenePicker.scenes.find((item) => !item.sceneConfig?.photoSurface)
        if (scene) this.scenePicker.selection = { sceneVariantId: scene.id, scenePhotoAssetId: null, sceneTemplateDigest: scene.sceneTemplateDigest ?? null }
      }
      this.setActiveView('creation')
      this.successView.focus({ preventScroll: true })
      this.scrollGeneratorIntoView()
      this.updateCommerce()
    }

    beginSecondPerson(personIndex) {
      if (this.deleteBusy || (!this.localMockEnabled && !this.gatewayEnabled) || !this.gatewayReady || (this.personCount < 1 || this.personCount >= 5)) return
      if (this.secondState === 'succeeded' || this.readyPeople.has(this.pendingPersonIndex)) this.resetAdditionalForm()
      if (!personIndex) this.analytics?.action('person_add_click', { person_count_before: this.personCount, max_person_count: 5, entry_point: 'add_person' })
      this.pendingPersonIndex = personIndex || PERSON_POSITIONS.find((person) => !this.readyPeople.has(person)) || 2
      this.secondUploadTitle.textContent = `Choose a photo of person ${this.personCount + 1}`
      this.secondDefaultButtonLabel = `Generate person ${this.personCount + 1}`
      this.setSecondStatus(`Choose a photo, then generate person ${this.personCount + 1}.`)
      this.setActiveView('create')
      this.secondDropzone.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }

    showSecondSuccess(personIndex = this.pendingPersonIndex) {
      this.abortGeneration()
      this.readyPeople.add(personIndex)
      this.personCount = this.readyPeople.size
      this.selectedPeople.add(personIndex)
      this.previewPerson = personIndex
      this.setSecondStatus(`${this.personCount} people are ready. Choose a scene or continue with the figures only.`)
      this.setSecondState('succeeded')
      this.setActiveView('creation')
      this.successView.focus({ preventScroll: true })
      this.scrollGeneratorIntoView()
      this.updateCommerce()
    }

    openDeleteDialog(personIndex) {
      if (
        this.deleteBusy || this.commerceBusy || this.scenePicker?.busy ||
        !this.readyPeople.has(personIndex) ||
        ['generating', 'validating'].includes(this.currentState) ||
        ['generating', 'validating'].includes(this.secondState)
      ) return
      this.deletePersonIndex = personIndex
      this.deletePersonLabel.textContent = `Person ${[...this.readyPeople].indexOf(personIndex) + 1}`
      const source = this.resultImages[personIndex - 1]?.querySelector('img')
      if (source) {
        this.deleteThumbnail.removeAttribute('srcset')
        this.deleteThumbnail.removeAttribute('sizes')
        this.deleteThumbnail.src = source.currentSrc || source.src
      }
      this.deleteError.hidden = true
      this.deleteError.textContent = ''
      this.deleteDialog.showModal()
      this.analytics?.deletion(personIndex)
    }

    async confirmDeletePerson() {
      const personIndex = this.deletePersonIndex
      if (!personIndex || this.deleteBusy) return
      this.analytics?.deleteDecision('confirm')
      const snapshot = {
        readyPeople: new Set(this.readyPeople),
        selectedPeople: new Set(this.selectedPeople),
        manualEditing: this.manualEditing,
        manualEditingPeople: new Set(this.manualEditingPeople),
        previewPerson: this.previewPerson,
        activeView: this.activeView,
        currentState: this.currentState,
        secondState: this.secondState,
        sceneSelection: this.scenePicker ? { ...this.scenePicker.selection } : null,
      }
      const previous = this.remoteDesign
      this.deleteBusy = true
      this.deleteConfirmButton.disabled = true
      this.deleteDialog.close()
      // Keep files, object URLs and generation slots intact until the server confirms.
      this.readyPeople.delete(personIndex)
      this.selectedPeople.delete(personIndex)
      this.manualEditingPeople.delete(personIndex)
      if (!this.selectedPeople.size) this.manualEditing = false
      this.personCount = this.readyPeople.size
      if (!this.readyPeople.has(this.previewPerson)) this.previewPerson = [...this.readyPeople][0] || 1
      this.setSecondState('idle')
      if (!this.personCount) this.setState(this.file ? 'ready' : 'idle')
      this.setActiveView(this.personCount ? 'creation' : 'create')
      this.setState(this.currentState)
      this.updateCommerce()
      try {
        let design = previous
        if (this.gatewayEnabled) {
          await this.ensureEditableDesign()
          design = this.remoteDesign
          if (!design?.id || !Number.isInteger(design.revision)) {
            throw new Error('The saved design could not be identified. Refresh and try again.')
          }
          try {
            design = await this.gateway.request(
              `/customizations/${design.id}/people/${personIndex}`,
              { method: 'DELETE', body: { expectedRevision: design.revision } },
            )
          } catch (error) {
            if (error.code === 'REVISION_CONFLICT') throw error
            const current = await this.gateway.request(`/customizations/${design.id}`).catch(() => null)
            const remaining = design.people.filter((person) => person.personIndex !== personIndex)
            const matches = current?.status === 'draft' && current.revision === design.revision + 1 &&
              current.people.length === remaining.length && remaining.every((person) =>
                current.people.some((other) => other.personIndex === person.personIndex && other.taskId === person.taskId && other.assetId === person.assetId))
            if (!matches) {
              if (current && current.revision !== design.revision) throw new Error('This design changed. Reload it before deleting a person.')
              throw error
            }
            design = current
          }
        }
        this.applyDeletedPerson(personIndex, design)
      } catch (error) {
        this.readyPeople = snapshot.readyPeople
        this.selectedPeople = snapshot.selectedPeople
        this.manualEditing = snapshot.manualEditing
        this.manualEditingPeople = snapshot.manualEditingPeople
        this.personCount = this.readyPeople.size
        this.previewPerson = snapshot.previewPerson
        if (this.scenePicker) this.scenePicker.selection = snapshot.sceneSelection
        this.setState(snapshot.currentState)
        this.setActiveView(snapshot.activeView)
        this.setSecondState(snapshot.secondState)
        this.renderPreview()
        this.deleteError.textContent = error.message || 'This person could not be deleted. Try again.'
        this.deleteError.hidden = false
        this.analytics?.failed('person_delete', error)
        if (this.isConnected) { this.deleteDialog.showModal(); this.analytics?.deletion(personIndex) }
      } finally {
        this.deleteBusy = false
        this.deleteConfirmButton.disabled = false
        this.setState(this.currentState)
        this.setSecondState(this.secondState)
        this.updateCommerce()
      }
    }

    applyDeletedPerson(personIndex, design) {
      this.draftPersonIds[personIndex] = crypto.randomUUID()
      if (design) {
        this.remoteDesign = design
        this.manualEditing = Boolean(design.manualEditing)
        this.manualEditingPeople = new Set(design.manualEditingPersonIndexes ?? (design.manualEditing ? design.personIndexes : []))
      }
      this.readyPeople.delete(personIndex)
      this.selectedPeople.delete(personIndex)
      this.manualEditingPeople.delete(personIndex)
      if (!this.selectedPeople.size) this.manualEditing = false
      this.personCount = this.readyPeople.size
      const slot = this.remoteSlots[personIndex]
      if (slot) {
        slot.generationKey = crypto.randomUUID()
        slot.request = null
        slot.task = null
      }
      this.secondFile = null
      this.secondFileInput.value = ''
      this.secondUploadDefault.hidden = false
      this.secondUploadReady.hidden = true
      this.secondUploadValidating.hidden = true
      this.clearUploadError(2)
      this.setSecondState('idle')

      if (this.personCount === 0) {
        this.previewPerson = 1
        if (this.scenePicker) this.scenePicker.selection = { sceneVariantId: null, scenePhotoAssetId: null, sceneTemplateDigest: null }
        this.setState(this.file ? 'ready' : 'idle')
        this.setStatus(this.file ? MESSAGES.photoReady : 'Choose a photo to generate a new person.')
        this.setActiveView('create')
      } else {
        if (!this.readyPeople.has(this.previewPerson)) this.previewPerson = [...this.readyPeople][0]
        this.setActiveView('creation')
      }
      this.renderPreview()
      this.updateCommerce()
    }

    setActiveView(view) {
      if (view === 'creation' && this.personCount === 0) return
      if (view === 'create' && this.personCount > 0 && this.personCount < 5 && (this.secondState === 'succeeded' || this.readyPeople.has(this.pendingPersonIndex))) this.resetAdditionalForm()
      this.activeView = view
      const creating = view === 'create'
      this.uploadView.hidden = this.personCount > 0
      this.successView.hidden = this.personCount === 0
      this.renderEntryLayout()
      this.successView.dataset.formActive = String(creating)
      this.addPersonButton.hidden = creating || this.personCount === 5
      this.personCards.forEach((card) => {
        card.hidden = !this.readyPeople.has(Number(card.dataset.aiPersonCard))
      })
      this.secondForm.hidden = !creating || this.personCount === 0 || this.personCount >= 5
      this.resultCustomization.hidden = false
      this.secondFileInput.disabled = !!this.deleteBusy || this.secondState === 'generating' || this.personCount === 5
      this.setSecondState(this.secondState)
    }

    renderEntryLayout() {
      this.dataset.entryLayout = String(this.personCount === 0 && !['generating', 'failed', 'succeeded'].includes(this.currentState))
    }

    renderPeople() {
      // Slots identify saved assets; their insertion order determines visible numbering.
      const people = [...this.readyPeople]
      people.forEach((person, index) => {
        const number = index + 1
        const card = this.personCards[person - 1]
        if (card.parentElement.children[index] !== card) card.parentElement.insertBefore(card, card.parentElement.children[index])
        card.setAttribute('aria-label', `Figure ${number}, ${this.dataset.readyLabel}`)
        this.personDetails[person - 1].setAttribute('aria-label', `Preview Figure ${number}`)
        card.querySelector('[data-ai-figure-label]').textContent = `Figure ${number}`
        this.deletePersonButtons[person - 1].setAttribute('aria-label', `Delete Person ${number}`)
        const image = this.resultImages[person - 1]
        image.querySelector('img').alt = `Generated person ${number}`
        image.querySelector('[data-ai-source-photo]').alt = `Original photo for Figure ${number}`
        const input = this.manualPersonInputs[person - 1]
        input.setAttribute('aria-label', `Manual Editing for Figure ${number}`)
        const row = input.closest('[data-ai-manual-person-row]')
        row.querySelector('[data-ai-figure-label]').textContent = `Figure ${number}`
        if (row.parentElement.children[index] !== row) row.parentElement.insertBefore(row, row.parentElement.children[index])
      })
    }

    renderPreview() {
      this.renderPeople()
      const showingTaskState = ['generating', 'failed'].includes(this.secondState)
      const fallback = [...this.readyPeople][0]
      const shownPerson = this.readyPeople.has(this.previewPerson) ? this.previewPerson : fallback
      this.personDetails.forEach((button) => {
        button.setAttribute('aria-pressed', String(!showingTaskState && Number(button.dataset.aiPerson) === shownPerson))
        button.disabled = !!this.deleteBusy || showingTaskState
      })
      this.resultImages.forEach((image) => {
        image.hidden = showingTaskState || Number(image.dataset.aiResultPerson) !== shownPerson
      })
    }

    scrollGenerationIntoView() {
      if (!window.matchMedia('(max-width: 749px)').matches) return
      const section = this.querySelector('.ai-generator')
      if (!section) return
      // Keep the announcement, navigation and introduction above the progress card.
      const headerHeight = [...document.querySelectorAll('.shopify-section-group-header-group')]
        .reduce((height, element) => height + element.getBoundingClientRect().height, 0)
      window.scrollTo({
        top: Math.max(0, window.scrollY + section.getBoundingClientRect().top - headerHeight),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      })
    }

    scrollGeneratorIntoView() {
      this.querySelector('.ai-generator')?.scrollIntoView({ block: 'start' })
    }

    resetAdditionalForm() {
      this.uploadControllers[2]?.abort()
      this.pendingPersonIndex = PERSON_POSITIONS.find((person) => !this.readyPeople.has(person)) || 5
      this.secondFile = null
      this.secondFileInput.value = ''
      this.secondUploadDefault.hidden = false
      this.secondUploadReady.hidden = true
      this.secondUploadValidating.hidden = true
      this.clearUploadError(2)
      this.secondUploadTitle.textContent = `Choose a photo of person ${this.personCount + 1}`
      this.secondState = 'idle'
      this.setSecondStatus(`Choose a photo, then generate person ${this.personCount + 1}.`)
    }

    async initializeScenes() {
      const [{ ScenePicker }, { projectPhoto }] = await Promise.all([
        import(new URL(this.dataset.scenePickerUrl, window.location.href).href),
        import(new URL(this.dataset.sceneProjectionUrl, window.location.href).href),
      ])
      if (!this.isConnected) return
      const fallback = this.sceneButtons.map((button, index) => ({
        id: button.dataset.sceneVariantId || `local-${index}`,
        title: button.dataset.sceneTitle,
        amount: String(Number(button.dataset.scenePriceCents) / 100),
        currency: this.dataset.currency,
        imageUrl: button.querySelector('img')?.src ?? null,
        sceneConfig: JSON.parse(button.dataset.sceneConfig || 'null'),
        sceneTemplateDigest: null,
        available: true,
      }))
      const scenes = this.gatewayEnabled ? (this.catalog?.scenes ?? []).map((scene) => ({
        ...scene,
        imageUrl: scene.sceneConfig ? scene.imageUrl : fallback.find((item) => item.id === scene.id)?.imageUrl || scene.imageUrl,
      })) : fallback
      this.scenePicker = new ScenePicker(this, scenes, projectPhoto)
      this.updateCommerce()
    }

    async ensureEditableDesign() {
      if (this.remoteDesign?.status !== 'finalized') return
      this.remoteDesign = await this.gateway.request(`/customizations/${this.remoteDesign.id}/editable`, {
        body: { expectedRevision: this.remoteDesign.revision },
      })
      // Generation attempts belong to the immutable source; new work targets the successor.
      for (const slot of Object.values(this.remoteSlots)) {
        slot.task = null
        slot.request = null
        slot.generationKey = crypto.randomUUID()
      }
    }

    async saveSceneSelection(selection) {
      if (!this.gatewayEnabled) return
      await this.ensureEditableDesign()
      if (!this.remoteDesign?.id || !this.selectedPeople.size) throw new Error('Select at least one generated person first.')
      const body = { manualEditing: this.manualEditing, manualEditingPersonIndexes: [...this.manualEditingPeople].sort(), expectedRevision: this.remoteDesign.revision, personIndexes: [...this.selectedPeople].sort(), ...selection }
      try {
        this.remoteDesign = await this.gateway.request(`/customizations/${this.remoteDesign.id}`, { method: 'PUT', body })
      } catch (error) {
        if (error.code === 'REVISION_CONFLICT') throw error
        // A lost response may already have committed. Resume only that exact save;
        // never overwrite another tab's version after a revision conflict.
        const current = await this.gateway.request(`/customizations/${this.remoteDesign.id}`).catch(() => null)
        if (current?.revision === body.expectedRevision + 1 && current.status === 'draft' && Boolean(current.manualEditing) === body.manualEditing && JSON.stringify(current.manualEditingPersonIndexes) === JSON.stringify(body.manualEditingPersonIndexes) && current.sceneVariantId === selection.sceneVariantId && current.scenePhotoAssetId === selection.scenePhotoAssetId && (current.sceneTemplateDigest ?? null) === (selection.sceneTemplateDigest ?? null) && JSON.stringify(current.personIndexes) === JSON.stringify(body.personIndexes)) this.remoteDesign = current
        else throw error
      }
    }

  }

  if (!customElements.get('ai-personalization-generator')) {
    customElements.define('ai-personalization-generator', AiPersonalizationGenerator)
  }
})()
